/** @file Auth/user logic */

import { fetchAuthSession, getCurrentUser, signOut } from 'aws-amplify/auth';

const API_URL = process.env.REACT_APP_API_URL;

/** localStorage key holding support-staff admin login state. */
export const ADMIN_INFO_STORAGE_KEY = 'adminInfo';

export interface AdminInfo {
  isAdminLogin?: boolean;
  adminEmail?: string;
}

/**
 * Read the stored admin login state. Missing or malformed storage reads as empty.
 *
 * @returns {AdminInfo} - Stored admin info, or an empty object
 */
export const readAdminInfo = (): AdminInfo => {
  try {
    return JSON.parse(window.localStorage.getItem(ADMIN_INFO_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

/**
 * Persist admin login state.
 *
 * @param {AdminInfo} adminInfo - Admin info to store
 */
export const writeAdminInfo = (adminInfo: AdminInfo): void => {
  window.localStorage.setItem(ADMIN_INFO_STORAGE_KEY, JSON.stringify(adminInfo));
};

/**
 * The customer email a support admin is currently acting as, if any.
 *
 * @returns {string} - The admin email, or an empty string
 */
export const getStoredAdminEmail = (): string => readAdminInfo().adminEmail || '';

export const getSession = async (): Promise<any> => {
  const sessionValues = (await fetchAuthSession()).tokens as any;
  sessionValues.adminEmail = readAdminInfo().adminEmail;
  return sessionValues;
};

/**
 * Get current user ID
 *
 * @returns {string} - Current user ID
 */
export const getUserId = async () => {
  const userInfo = await getCurrentUser();

  return userInfo.userId;
};

export const urlWithAdminQuery = (url: string, adminEmail: string) => {
  let output = API_URL + url;
  if (adminEmail) {
    url.includes('?') ? (output += '&') : (output += '?');

    return `${output}admin_email=${encodeURIComponent(adminEmail)}`;
  } else return output;
};

/**
 * End the current session and return to the login screen.
 *
 * Clears admin login state, signs out of Cognito, and reloads at the root. A
 * sign-out failure is logged and never blocks the redirect, so this always
 * leaves the user on a page they can act from.
 */
export const endSession = async (): Promise<void> => {
  window.localStorage.removeItem(ADMIN_INFO_STORAGE_KEY);
  try {
    await signOut();
  } catch (err) {
    console.error('Error during sign out:', err);
  } finally {
    window.location.assign('/');
  }
};
